"use client";

import { FundsDetailPage } from ".";
import { FundsHeaderProps } from "@/components/organisms/fundsDetailHeader";
import { FundsTagType } from "@/types/consts";
import createUmbracoMediaItemProps, {
  generateImageData,
} from "@/components/atoms/media/umbracoMediaElementPropsFactory";
import { GetUmbracoBaseUrl } from "@/utils/urlHelpers";
import { MediaImageData } from "@/components/atoms/media/MediaImage";
import { KeyPointsProps } from "@/components/molecules/keyPointsCard";
import {
  FundsDetailFactoryData,
  IntroductionBlockProperties,
} from "./FundsDetailPageType";
import { FundsContentBlocks } from "@/components/organisms/fundsContentBlocks";
import { PortfolioItemData } from "@/components/molecules/portfolioItem";
import PortfolioHighlights from "@/components/organisms/portfolioHighlights/PortfolioHighlights";

function isFundsDetailFactoryData(
  data: unknown,
): data is FundsDetailFactoryData {
  return (
    typeof data === "object" &&
    data !== null &&
    "blocks" in data &&
    Array.isArray(data.blocks)
  );
}

function FundsDetailPageFactory({ data }: { data: unknown }) {
  if (!isFundsDetailFactoryData(data)) {
    return null;
  }

  const mediaItem = data?.header?.headerImage?.items?.[0]?.content;
  const mediaItemProps = createUmbracoMediaItemProps({
    mediaItemContent: mediaItem,
    umbracoBaseUrl: GetUmbracoBaseUrl(),
  });

  const primaryContact = data?.header?.primairyContact?.[0];
  const contactMediaData = primaryContact?.properties?.profilePicture
    ? createUmbracoMediaItemProps({
        mediaItemContent: {
          contentType: "imageComponent",
          properties: {
            image: primaryContact.properties.profilePicture,
          },
        },
        umbracoBaseUrl: GetUmbracoBaseUrl(),
      })
    : undefined;

  const mappedKeyPoints: KeyPointsProps[] =
    data?.header?.highlights?.items?.map((item) => {
      const { icon, title, bullets } = item.content.properties;

      return {
        icon: icon,
        title,
        items: bullets,
      };
    }) ?? [];

  const download = data?.header?.download?.[0];
  const isDownload = download?.url?.startsWith("/media/") ? true : false;
  const urlOrPath = download?.url ?? download?.route?.path;
  const downloadUrl = isDownload ? GetUmbracoBaseUrl() + urlOrPath : urlOrPath;

  const headerData: FundsHeaderProps = {
    title: data.header?.headertitle?.markup ?? "",
    intro: data?.header?.introText?.markup,
    mediaData: mediaItemProps,
    openForInvestment: data?.header?.openForInvestment ?? false,
    tags: [
      {
        type: FundsTagType.MinimumInvestment,
        value: data?.header?.minimumInvestment?.[0]?.name ?? "",
      },
      {
        type: FundsTagType.InvestmentType,
        value: data?.header?.investmentType?.[0]?.name ?? "",
      },
      {
        type: FundsTagType.TargetAudience,
        value: data?.header?.targetAudience?.[0]?.name ?? "",
      },
      {
        type: FundsTagType.RiskReturnProfile,
        value: data?.header?.riskReturnProfile?.[0]?.name ?? "",
      },
      {
        type: FundsTagType.PropertyType,
        value: data?.header?.propertyType?.[0]?.name ?? "",
      },
    ].filter((tag) => tag.value.trim() !== ""),
    features:
      data?.header?.fundsStats?.items?.map((item) => ({
        title: item?.content?.properties?.propertyName ?? "",
        info: item?.content?.properties?.propertyValue ?? "",
      })) ?? [],
    detailPoints: {
      text: data?.header?.downloadText?.markup,
      download: {
        url: downloadUrl,
        isDownloadLink: isDownload,
        target: download?.target,
        title: download?.title,
      },
    },
    contactTitle: data?.header?.contactTitle,
    contactSubtitle: data?.header?.contactSubtitle,
    contactDetails: primaryContact
      ? {
          firstName: primaryContact?.properties?.firstName ?? "",
          lastName: primaryContact?.properties?.lastName ?? "",
          jobTitle: primaryContact?.properties?.jobtitle ?? "",
          mediaData: contactMediaData as MediaImageData,
          email: primaryContact?.properties?.socialMedia?.items?.find(
            (item) => item?.content?.contentType === "email",
          )?.content?.properties?.eMailAddress,
          phone: primaryContact?.properties?.socialMedia?.items?.find(
            (item) => item?.content?.contentType === "phone",
          )?.content?.properties?.phoneNumber,
        }
      : undefined,
    keyPoints: mappedKeyPoints,
  };

  const mappedPortfolioItems: PortfolioItemData[] =
    data.portfolioHighlights?.map((item) => {
      const content = item.properties;
      const header = content.header?.items?.[0]?.content?.properties;

      const overviewImage = content.overviewImage?.[0];
      const headerImage =
        header?.mediaItem?.items?.[0]?.content?.properties?.image?.[0];
      const imageData = overviewImage ?? headerImage;

      const mediaItem = imageData
        ? generateImageData(
            {
              image: [imageData],
            },
            GetUmbracoBaseUrl(),
          )
        : undefined;

      const introductionBlock = content.contentBlocks?.items?.find(
        (block) => block.content?.contentType === "introductionComponent",
      );
      const introductionProperties = introductionBlock?.content
        ?.properties as IntroductionBlockProperties;

      return {
        title: header?.title?.markup ?? item.name,
        introduction: introductionProperties?.introductionText?.markup,
        url: item.route?.path ?? "",
        mediaItem,
        type: header?.type,
        location: header?.location,
        surface: header?.surface,
        status: header?.status,
      };
    }) ?? [];

  const contentBlocks = <FundsContentBlocks blocksData={data.blocks} />;

  const portfolioHighlightsBlock = (
    <PortfolioHighlights
      title={data?.header?.portfolioTitle ?? ""}
      items={mappedPortfolioItems}
    />
  );

  return (
    <FundsDetailPage
      header={headerData}
      contentBlocks={contentBlocks}
      portfolioHighlights={portfolioHighlightsBlock}
    />
  );
}

export default FundsDetailPageFactory;
